// utils.js
import _ from "lodash-es"
import { nanoid } from "nanoid"

import { SortDirectionType } from "../utils/constants.js"
import { submitLogout } from "./api.js"

/* An approximation of a PHP function that inverts key/value pairs so array_flip({1: 'a', 2: 'b'}) = {a: 1, b: 2}*/
export const array_flip = function (arr) {
    return Object.keys(arr).reduce((accumulator, key) => {
        accumulator[arr[key]] = key
        return accumulator;
    }, {})
}

// helper data load functions that fetch data and sets state. caller to handle exceptions
export const simulateAPICall = (jsonData) => new Promise((resolve) => setTimeout(() => resolve(jsonData), 1000));

export const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0 
    && obj.constructor === Object;
};

//parses a json.net date string into a JavaScript Date object
export const deserializeJsonDotNetDate = (value) => {
    const dateRegex = /\/Date\((\d+)\+\d+\)\//;
    let result = ''
    if (!value) {
        return result
    }
    let match = value.match(dateRegex)
    if (match && match[1]) {
        return new Date(parseInt(match[1], 10))
    }
    return result
}

export const compareObjects = (obj1, obj2) => {
    try {
        if (obj1 === obj2) {
            return true; // for primatives including null/undefined, or both point to the same object
        }
        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
            return obj1 === obj2; // compare non-object types directly
        }
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        if (keys1.length !== keys2.length) {
            return false;
        }
        for (let key of keys1) {
            if (!compareObjects(obj1[key], obj2[key])) {
                return false;
            }
        }
        return true;
    }
    catch (error) {
        console.error("utils.compareObjects Exception: ", error.message);
        return false;
    }
}

export const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
}

// compares two utg string versions and returns -1, 0, 1
// e.g. (5.0.0.3200, 5.0.0.3130) -> returns 1
// e.g. (5.0.0.3130, 5.0.0.3200) -> returns -1
// e.g. (5.0.0.3200, 5.0.0.3200) -> returns 0
export const compareUtgVersions = (utg1, utg2) => {
    try {
        const utg1VersionSections = utg1.split('.')
        const utg2VersionSections = utg2.split('.')
        if (utg1VersionSections.length != utg2VersionSections.length) {
            throw new Error('utg sections do not have same length'); // return immediately if we don't have the same number of sections
        }
        for (let i = 0; i < utg1VersionSections.length; i++) {
            const v1 = parseInt(utg1VersionSections[i], 10)
            const v2 = parseInt(utg2VersionSections[i], 10)
            if (v1 < v2) {
                return -1
            } else if (v1 > v2) {
                return 1
            }
        }
        return 0
    } catch (error) {
        console.log(error)
        throw error
    }
}

// expect queryParameters to be an array of arrays. The inner array should have two items. Index 0 is the key and index 1 is the value.
export const buildUrlWithQueryParameters = (baseUrl, queryParameters) => {
    const queryString = queryParameters.map((item, index) => {
        return `${index == 0 ? '?' : '&'}${item[0]}=${item[1]}`
    })
    return `${baseUrl}${queryString.join('')}`
}

export const checkApiInterfaceConflicts = (apiInterfaces) => {
    let existingPorts = apiInterfaces.map((apiInterface) => apiInterface.listenPort)
    return checkDuplicatesInArray(existingPorts)
}

export const checkDeviceDescriptionConflicts = (devices) => {
    let existingTaskDesc = devices.map((device) => device.taskDesc)
    return checkDuplicatesInArray(existingTaskDesc)
}

export const checkDuplicatesInArray = (array) => {
    for (let i = 0; i < array.length; i++) {
        for (let j = i + 1; j < array.length; j++) {
            if (array[i] === array[j]) {
                return true; // Duplicate found
            }
        }
    }
    return false; // No duplicates
}

export const generateJsonFromSchema = (schema) => {

    const generate = (schema) => {
        const result = {};

        if (schema.type === 'object' && schema.properties) {
            for (const key in schema.properties) {
                const property = schema.properties[key];
                if (property.default !== undefined) {
                    result[key] = property.default;
                } else if (property.type === 'object') {
                    result[key] = generate(property);
                }
            }
        }

        return result;
    }

    const jsonData = generate(schema)

    // make sure we have apiInterfaceConfig and deviceConfig objects and child arrays
    jsonData.config.apiInterfaceConfig = jsonData.config.apiInterfaceConfig || {}
    jsonData.config.apiInterfaceConfig.apiInterface = jsonData.config.apiInterfaceConfig.apiInterface || []

    jsonData.config.deviceConfig = jsonData.config.deviceConfig || {}
    jsonData.config.deviceConfig.device = jsonData.config.deviceConfig.device || []

    // api interfaces need an id assigned
    let apiInterfaces = jsonData.config.apiInterfaceConfig.apiInterface
    apiInterfaces.map((apiInterface) => {
        apiInterface.ccid = apiInterface.ccid ? apiInterface.ccid : nanoid()
    })

    return jsonData
}

export const saveFile = (data, fileName, mime_type=null) => {
    try {
        const byteArray = new Uint8Array(data);
        /* Package data as Blob for download. See:
            - https://stackoverflow.com/questions/3665115/how-to-create-a-file-in-memory-for-user-to-download-but-not-through-server
            - https://developer.mozilla.org/en-US/docs/Web/API/Blob)
        */
        if (mime_type == null) {
            mime_type = "application/octet-stream"
        }
        const blob = new Blob([byteArray], {
            type: mime_type
        })

        // Trigger the download using a temporary link
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName // Set the file name
        document.body.appendChild(link)
        link.click();

        // Clean up by removing the link and revoking the object URL
        link.remove();
        URL.revokeObjectURL(link.href)
    } catch (error) {
        console.error("utils.saveFile Exception: ", error.message);
        throw error
    }
}

export const fileToBinaryString = async function (file) {
    return new Promise((resolve, reject) => {
        try {
            const reader = new FileReader();
            reader.onload = (e) => {
                resolve(e.target.result);
            }
            reader.onerror = (e) => {
                reject(e);
            }
            reader.readAsBinaryString(file);
        } catch (x) {
            console.trace(x.toString())
            window.alert(x.toString())
        }
    });
}

/**
 * Return a string to be used in the body of an REST API request that accepts filters. 
 * Builds correct structure and applies formatting required by the REST API.
 * @param {Array} filter : Array of filters to be applied to request
 */
export const formatFilterRequestBody = (filter) => {
    // The REST API requires that the filter payload package be encoded as a string with special characters escaped or removed.
    // Rules:
    // Surrounding quotes ""
    // Escape in-line quotes \"
    // Escape in-line backslash \\
    // And remove newlines
    const filterObject = `{"Predicates": ${JSON.stringify(filter)}}` // manually build Predicates
    const formattedFilterObject = filterObject.replace(/"/g, '\\"')
    return `"${formattedFilterObject}"`
}

/**
 * Return a string encoding the files as multipart form data. Useful for intercepting a submit event on a form that uploads files.
 * @param {string} boundary : for separating request body segments
 * @param {object} files : associative array that maps form input name attributes to HTML5 File objects, e.g. {file1:<File>, file2:<File>}
 */
export const multipartFormDataEncodeFiles = async function (boundary, files) {
    try {
        let encoded = "";
        for (const input_name of Object.keys(files)) {
            let file = files[input_name];
            if (!(file instanceof File)) {
                throw new Error("file with name attribute \"".concat(input_name).concat("\" is not a File"));
            }
            let contents = await fileToBinaryString(file);
            encoded = encoded.concat(`${boundary}
Content-Disposition: form-data; name="Content-Type"

${file.type}
${boundary}
Content-Disposition: form-data; name="${input_name}"; filename="${file.name}"
Content-Type: ${file.type}

${contents}
`);
        }
        encoded = encoded.concat(`${boundary}--
`);
        return encoded
    } catch (x) {
        console.trace(x.toString())
        window.alert(x.toString())
    }
}

export const convertUrlReactToEmber = function (config, url_string = null) {
    let print = false
    if (url_string == null) {
        url_string = window.location.href
    }
    const url = new URL(url_string)
    const splat = url.pathname.split('/')
    const directory = splat.length && splat.length > 3 && splat[3] == "dashboard" ? "/dashboard" : splat.join('/')
    const origin = config && config.value && config.value.in4mBaseUrl ? config.value.in4mBaseUrl : "https://".concat(url.hostname).concat(url.port ? ":".concat(url.port) : "")
    const new_url = origin.concat("/legacy/#").concat(directory).concat(url.searchParams.size > 0 ? "?".concat(url.searchParams.toString()) : "")
    if (print) {
        console.log("Converted react url ".concat(url_string).concat(" to ember url ").concat(new_url))
    }
    return new_url
}

export const iconForOrgType = function(businessEntity){
    if (businessEntity) {
        if (businessEntity.isMerchant) {
            return 'user'
        }
        else if (businessEntity.isOrganization) {
            return 'globe-americas'
        }
        else if (businessEntity.isResellerOrganization) {
            return 'briefcase'
        }
    }
    return null
}

export const get_tabs = function(tab_count){
    let tabs = ""
    for (let i = 0; i < tab_count; i++) {
        tabs = tabs.concat("\t")
    }
    return tabs
}

/*
Replacement function for JSON.stringify that doesn't throw an error on cyclic object references.
Set the second parameter to true to abbreviate arrays.
*/
export const stringify_recursive = function(obj, collapse_arrays=false){
    const visited = new WeakSet()
    function recursive(node, tab_count=1) {
        if (typeof node !== 'object' || node == null) {
            return JSON.stringify(node)
        }
        else if (visited.has(node)) {
            return "<Cyclic object reference>"
        }
        let bracket1
        let bracket2
        const is_arr = Array.isArray(node)
        if (is_arr) {
            if (collapse_arrays) {
                return "<Array of length ".concat(node.length).concat(">")
            }
            bracket1 = "["
            bracket2 = "]"
        }
        else {
            bracket1 = "{"
            bracket2 = "}"
        }
        visited.add(node)
        let str = ""
        let nonempty = false
        let tabs = get_tabs(tab_count)
        for (const key in node) {
            if (Object.prototype.hasOwnProperty.call(node, key)) {
                if (str !== "") {
                    str = str.concat(",")
                }
                str = str.concat("\n")
                nonempty = true
                str = str.concat(tabs)
                if (!is_arr) {
                    str = str.concat(key).concat(": ")
                }
                str = str.concat(recursive(node[key], tab_count + 1))
            }
        }
        if (nonempty) {
            str = str.concat("\n").concat(get_tabs(tab_count - 1))
        }
        return bracket1.concat(str).concat(bracket2)
    }
    return recursive(obj)
}

export const isPreactSignal = function(val){
    return typeof val === 'object' && val !== null && 'value' in val
}

export const computeSortedData = function (filteredDataSignal, sortColumnSignal, sortDirectionSignal = null){ 
    return function () {
        // this function supports signals or strings for sort column name
        let sortColumn
        if (isPreactSignal(sortColumnSignal)) {
            sortColumn = sortColumnSignal.value
        }
        else if (typeof sortColumnSignal == 'string') {
            sortColumn = sortColumnSignal
        }
        else {
            sortColumn = ''
        }
        // just return filtered data if no sorting
        if (!sortColumn || !sortColumn.length) {
            return [...filteredDataSignal.value]
        }
        // supports signals and strings for sort direction
        let direction
        if (isPreactSignal(sortDirectionSignal)) {
            direction = sortDirectionSignal.value
        }
        else if (typeof sortDirectionSignal == 'string') {
            direction = sortDirectionSignal
        }
        else {
            direction = SortDirectionType.ASCENDING
        }
        let _sortedData = [...filteredDataSignal.value]
        return _sortedData.sort((a, b) => {
            let _a = _.get(a, sortColumn, null)
            let _b = _.get(b, sortColumn, null)
            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''
            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }
            //console.log("(_a, _b) = (".concat(_a).concat(", ").concat(_b).concat(")"))
            if (direction == SortDirectionType.ASCENDING) {
                if (_a < _b) {
                    //console.log("b comes first ascending")
                    return -1
                }
                if (_a > _b) {
                    //console.log("a comes first ascending")
                    return 1
                }
                return 0
            }
            else {
                if (_a > _b) {
                   // console.log("b comes first descending")
                    return -1
                }
                if (_a < _b) {
                    //console.log("a comes first descending")
                    return 1
                }
                return 0
            }
        })
    }
}

export const hasPermission = (userPermissions, key, action) => {
    const p = userPermissions.find(element => element.key == key)
    return p[action]
}

/* Parses an arbitrary string value. Used for retrieving data from localStorage, where values are serialized in a form that makes primitive types except for null unreadable by JSON.parse() */
export const parseString = (str) => {
    if (str == null) {
        return null
    }
    else if (typeof str != 'string') {
        throw new Error("parseJSON(): invalid input parameter of type ".concat(typeof str))
    }
    try {
        return JSON.parse(str)
    }
    catch {
        if (/^(-?\d+)?\.\d+$/.test(str)) {
            if (/^-?\d+$/.test(str)) {
                return parseInt(str)
            }
            return parseFloat(str)
        }
        else if (str == "true") {
            return true
        }
        else if (str == "false") {
            return false
        }
        else if (str == "null") { //might be unnecessary
            return null
        }
        return str
    }
}

export const makeFieldsReadonly = (schema) => {
  if (typeof schema !== "object" || schema === null) return schema

  // Deep copy to avoid mutating the original schema
  const readonlyFieldsSchema = Array.isArray(schema) ? [] : {}

  for (const key in schema) {
    const value = schema[key]

    if (key === "properties" && schema.type === "object") {
      readonlyFieldsSchema[key] = {}
      for (const propName in value) {
        const prop = value[propName]

        if (prop.type === "object") {
          readonlyFieldsSchema[key][propName] = makeFieldsReadonly(prop)
        } else {
          readonlyFieldsSchema[key][propName] = {
            ...prop,
            "readonly": true
          }
        }
      }
    } else {
      readonlyFieldsSchema[key] = makeFieldsReadonly(value)
    }
  }

  return readonlyFieldsSchema
}

export const isset = function(val){
    return typeof val !== 'undefined' && val != null && val != ''
}

export const logout = async (config, navigate) => {
    const url = await submitLogout(config)
    window.localStorage.removeItem('accessToken')
    navigate('/')
}

export const getDateMinus30DaysComputed = () => {
    const now = new Date(); // Current date and time
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // Subtract 30 days in milliseconds
    return thirtyDaysAgo.toISOString(); // Format as ISO string
}

export const validateForm = (form, validators) => {
    let valid = true
    if (form.elements && form.elements.length) {
        for (let i = 0; i < form.elements.length; i++) {
            const input = form.elements[i]
            if (!validateInput(input, validators)) {
                valid = false
            }
        }
    }
    return valid
}

export const naturalValidateInput = (input, report = true) => {
    let error_msgs = []
    if (input.validationMessage && input.validationMessage.length) {
        error_msgs.push(input.validationMessage)
    }
    if (input.validityState) {
        if (input.validityState.badInput) {
            error_msgs.push("Bad input.")
        }
        if (input.validityState.patternMismatch) {
            error_msgs.push("Invalid input.")
        }
        if (input.validityState.rangeOverflow) {
            error_msgs.push("Input exceeds maximum value ".concat(input.getAttribute("max")))
        }
        if (input.validityState.rangeUnerflow) {
            error_msgs.push("Input contains less than minimum value ".concat(input.getAttribute("min")))
        }
        if (input.validityState.stepMispatch) {
            error_msgs.push("Input value must be aligned to the nearest multiple of ".concat(input.getAttribute("step")))
        }
        if (input.validityState.tooLong) {
            error_msgs.push("Length of input exceeds maximum of ".concat(input.getAttribute("maxlength")).concat(" characters."))
        }
        if (input.validityState.tooShort) {
            error_msgs.push("Value is too short (minimum ".input.getAttribute("minlength").concat(" characters)."))
        }
        if (input.validityState.typeMismatch) {
            error_msgs.push("Input type mismatch.")
        }
        if (input.validityState.valueMissing) {
            error_msgs.push("This field is required.")
        }
        input.setCustomValidity(error_msgs.join(" "))
    }
    else {
        return true
    }
    if (report) {
        return input.reportValidity()
    }
} 

export const validateInput = async (input, validators, type=null, recursive=false) => {
    if (!recursive) {
        //clean up from previous validation
        input.removeAttribute("title")
        input.setCustomValidity('')
        if (type == null) {
            naturalValidateInput(input, false)
        }
    }

    let error_msgs = []
    const prev_error = input.validationMessage
    if (prev_error.length && prev_error.length > 0) {
        error_msgs.push(prev_error)
    }

    if (input.hasAttribute('data-validators')) {
        const validator_keyvalues = JSON.parse(input.getAttribute('data-validators'))
        console.log(validator_keyvalues)
        if (type == null) {
            console.log("Did not specify a validator type, will run all of them")
            for (let key in validator_keyvalues) {
                if (key == null) {
                    console.error("Error, null validator type")
                    continue
                }
                else if (!(key in validator_keyvalues)) {
                    console.log("There are no validators for events of type ".concat(key))
                    continue
                }
                console.log("Calling function recursively on validators of type ".concat(key))
                validateInput(input, validators, key, true)
            }
        }
        else {
            if (!(type in validator_keyvalues)) {
                console.log("There are no validators for events of type ".concat(type))
                return
            }
            console.log("About to run event handlers for event of type ".concat(type))
            const validator_names = validator_keyvalues[type]
            if (validator_names && validator_names.length && validator_names.length > 0) {
                let validity = true
                let already_validated = {}
                for (let j = 0; j < validator_names.length; j++) {
                    const validator_name = validator_names[j]
                    if (validator_name in validators) {
                        if (validator_name in already_validated) {
                            console.log("ALready validated ".concat(validator_name))
                            continue
                        }
                        already_validated[validator_name] = type
                        const validator = validators[validator_name]
                        if (!validator) {
                            console.error("Validator \"".concat(validator_name).concat("\" is null"))
                            validity = false
                            error_msgs.push("Invalid validator \"".concat(validator_name).concat("\""))
                        }
                        else if (typeof validator != 'function') {
                            console.error("Validator \"".concat(validator_name).concat("\" is not a function"))
                            return
                        }
                        console.log("About to execute validator ".concat(validator_name))
                        try {
                            if (!validator(input)) {
                                console.log("Validator \"".concat(validator_name).concat("\" failed"))
                                validity = false
                                error_msgs.push(input.getAttribute('data-msg'))
                            }
                        }
                        catch (error) {
                            console.log("Validator threw the following error: \"".concat(error.toString()).concat("\""))
                            validity = false
                            error_msgs.push(error.toString())
                        }
                    }
                    else {
                        const msg = "Instant validator \"".concat(validator_name).concat("\" is undefined")
                        console.error(msg)
                        validity = false
                        error_msgs.push(msg)
                    }
                }
                if (!validity) {
                    const title = error_msgs.join(" ")
                    input.setCustomValidity(title)
                    if (!recursive) {
                        input.setAttribute("title", title)
                    }
                }
            }
            else {
                console.log("There are no validators of type ".concat(type))
                console.log(validator_names)
            }
        }
    }
    else {
        console.log("No validators for input ".concat(input.getAttribute("name")))
        console.log(input)
    }
    if (!recursive) {
        return input.reportValidity()
    }
    return !input.validationMessage || input.validationMessage.length == 0
}

